import React, { useMemo, useState } from "react";
import { CaretUp, CaretDown, CaretUpDown } from "@phosphor-icons/react";
import { TYPE_BY_VALUE } from "@/components/pinboard/constants";

/**
 * Excel-style sortable table for notes.
 * Columns are predefined; user can sort by clicking the header.
 *
 * Props:
 *   notes: array of note objects
 *   variant: "completed" | "deleted"
 *   actions: optional render(note) → JSX shown in actions column
 */
function fmtDateTime(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function getRowValue(note, key) {
  switch (key) {
    case "seq":
      return note.seq || "";
    case "type":
      return TYPE_BY_VALUE[note.type]?.label || note.type || "";
    case "customer":
      return note?.data?.customer || "";
    case "code":
      return note?.data?.code || "";
    case "phone":
      return note?.data?.phone || "";
    case "description":
      // For order-items, render text list joined by ' • '
      if (
        note.type === "order" &&
        Array.isArray(note?.data?.items) &&
        note.data.items.length > 0
      ) {
        return note.data.items.map((i) => i.text).join(" • ");
      }
      return note?.data?.description || "";
    case "charge":
      return note?.data?.charge ? `${note.data.charge} €` : "";
    case "channel":
      return (note.channels || []).join(", ");
    case "assigned_to":
      return note.assigned_to || "";
    case "executed_by":
      return note.executed_by || note.in_progress_by || "";
    case "completed_at":
      return note.completed_at || "";
    case "deleted_at":
      return note.deleted_at || "";
    case "created_at":
      return note.created_at || "";
    case "status": {
      // Priority sort: in_progress (4) > needs_payment (2) > read/ΑΝΑΜΟΝΗ (1)
      return (
        (note.in_progress ? 4 : 0) +
        (note.needs_payment ? 2 : 0) +
        (note.read ? 1 : 0)
      );
    }
    default:
      return "";
  }
}

const COMPLETED_COLUMNS = [
  { key: "seq", label: "#", width: "70px", mono: true },
  { key: "type", label: "ΤΥΠΟΣ", width: "130px" },
  { key: "customer", label: "ΠΕΛΑΤΗΣ", grow: true },
  { key: "code", label: "ΚΩΔΙΚΟΣ", width: "120px" },
  { key: "description", label: "ΕΙΔΗ / ΠΕΡΙΓΡΑΦΗ", grow: true, wide: true },
  { key: "charge", label: "ΧΡΕΩΣΗ", width: "90px", align: "right" },
  { key: "executed_by", label: "ΕΚΤΕΛΕΣΕ", width: "110px" },
  { key: "completed_at", label: "ΗΜΕΡ. ΕΚΤΕΛ.", width: "120px", isDate: true, defaultSort: "desc" },
];

const ACTIVE_COLUMNS = [
  { key: "seq", label: "#", width: "60px", mono: true },
  { key: "type", label: "ΤΥΠΟΣ", width: "110px" },
  { key: "customer", label: "ΠΕΛΑΤΗΣ", grow: true },
  { key: "code", label: "ΚΩΔΙΚΟΣ", width: "90px" },
  { key: "phone", label: "ΤΗΛΕΦΩΝΟ", width: "110px" },
  { key: "status", label: "ΚΑΤΑΣΤΑΣΗ", width: "160px", isStatus: true },
  { key: "description", label: "ΕΙΔΗ / ΠΕΡΙΓΡΑΦΗ", grow: true, wide: true },
  { key: "charge", label: "ΧΡΕΩΣΗ", width: "70px", align: "right" },
  { key: "channel", label: "ΚΑΝΑΛΙ", width: "80px" },
  { key: "assigned_to", label: "ΑΝΑΘΕΣΗ", width: "100px" },
  { key: "created_at", label: "ΔΗΜΙΟΥΡΓΙΑ", width: "115px", isDate: true, defaultSort: "desc" },
];

const DELETED_COLUMNS = [
  { key: "seq", label: "#", width: "70px", mono: true },
  { key: "type", label: "ΤΥΠΟΣ", width: "130px" },
  { key: "customer", label: "ΠΕΛΑΤΗΣ", grow: true },
  { key: "code", label: "ΚΩΔΙΚΟΣ", width: "120px" },
  { key: "description", label: "ΕΙΔΗ / ΠΕΡΙΓΡΑΦΗ", grow: true, wide: true },
  { key: "charge", label: "ΧΡΕΩΣΗ", width: "90px", align: "right" },
  { key: "executed_by", label: "ΕΚΤΕΛΕΣΕ", width: "110px" },
  { key: "deleted_at", label: "ΗΜΕΡ. ΔΙΑΓΡ.", width: "120px", isDate: true, defaultSort: "desc" },
];

export default function NotesTable({ notes, variant = "completed", actions }) {
  const columns =
    variant === "deleted"
      ? DELETED_COLUMNS
      : variant === "active"
      ? ACTIVE_COLUMNS
      : COMPLETED_COLUMNS;
  const defaultCol = columns.find((c) => c.defaultSort) || columns[columns.length - 1];
  const [sortKey, setSortKey] = useState(defaultCol.key);
  const [sortDir, setSortDir] = useState(defaultCol.defaultSort || "desc");
  // Per-column search: { [colKey]: string }; status uses "all" | "progress" | "charge" | "anamoni"
  const [filters, setFilters] = useState({});

  const setFilter = (key, val) =>
    setFilters((prev) => ({ ...prev, [key]: val }));
  const clearFilters = () => setFilters({});
  const hasAnyFilter = Object.values(filters).some((v) => v && String(v).length > 0);

  const filtered = useMemo(() => {
    return notes.filter((n) => {
      for (const c of columns) {
        const raw = filters[c.key];
        if (!raw) continue;
        if (c.isStatus) {
          if (raw === "progress" && !n.in_progress) return false;
          if (raw === "charge" && !n.needs_payment) return false;
          if (raw === "anamoni" && !n.read) return false;
          if (raw === "none" && (n.in_progress || n.needs_payment || n.read))
            return false;
          continue;
        }
        const cell = getRowValue(n, c.key);
        const display = c.isDate ? fmtDateTime(cell) : String(cell || "");
        if (
          !display
            .toString()
            .toLocaleLowerCase("el")
            .includes(String(raw).toLocaleLowerCase("el"))
        ) {
          return false;
        }
      }
      return true;
    });
  }, [notes, filters, columns]);

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sortKey) || defaultCol;
    const dir = sortDir === "asc" ? 1 : -1;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = getRowValue(a, col.key);
      const bv = getRowValue(b, col.key);
      // Dates: ISO strings compare lexicographically OK
      if (col.isDate) {
        return ((av || "") > (bv || "") ? 1 : (av || "") < (bv || "") ? -1 : 0) * dir;
      }
      // Numeric (seq, charge) fallback to localeCompare
      const an = parseFloat(av);
      const bn = parseFloat(bv);
      if (!Number.isNaN(an) && !Number.isNaN(bn)) {
        return (an - bn) * dir;
      }
      return String(av || "").localeCompare(String(bv || ""), "el", {
        sensitivity: "base",
      }) * dir;
    });
    return arr;
  }, [filtered, sortKey, sortDir, columns, defaultCol]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <div
      className="rounded-xl border border-black/10 bg-white overflow-x-auto shadow-sm"
      data-testid={`notes-table-${variant}`}
    >
      <table className="w-full text-xs font-arial border-collapse">
        <thead className="bg-ink text-white sticky top-0">
          <tr>
            {columns.map((c) => {
              const active = c.key === sortKey;
              const Icon = !active
                ? CaretUpDown
                : sortDir === "asc"
                ? CaretUp
                : CaretDown;
              return (
                <th
                  key={c.key}
                  onClick={() => toggleSort(c.key)}
                  data-testid={`table-${variant}-header-${c.key}`}
                  style={c.width ? { width: c.width, minWidth: c.width } : undefined}
                  className={`px-2.5 py-2 select-none cursor-pointer uppercase tracking-wider text-[10px] font-black border-r border-white/10 last:border-r-0 hover:bg-ink/85 transition-colors ${
                    c.align === "right" ? "text-right" : "text-left"
                  }`}
                >
                  <div
                    className={`inline-flex items-center gap-1 ${
                      c.align === "right" ? "justify-end" : ""
                    }`}
                  >
                    <span>{c.label}</span>
                    <Icon
                      size={11}
                      weight={active ? "bold" : "regular"}
                      className={active ? "text-amber-300" : "text-white/40"}
                    />
                  </div>
                </th>
              );
            })}
            {actions && (
              <th className="px-2.5 py-2 text-right uppercase tracking-wider text-[10px] font-black w-[110px]">
                ΕΝΕΡΓΕΙΕΣ
              </th>
            )}
          </tr>
          {/* Per-column search / filter row */}
          <tr className="bg-ink/95">
            {columns.map((c) => (
              <th
                key={`f-${c.key}`}
                style={c.width ? { width: c.width, minWidth: c.width } : undefined}
                className="px-1.5 py-1.5 border-r border-white/10 last:border-r-0 border-t border-white/10"
              >
                {c.isStatus ? (
                  <select
                    value={filters[c.key] || ""}
                    onChange={(e) => setFilter(c.key, e.target.value)}
                    data-testid={`table-${variant}-filter-${c.key}`}
                    className="w-full rounded-md bg-white/95 text-ink text-[10px] font-bold uppercase tracking-wider px-1.5 py-1 outline-none border border-white/20 focus:border-amber-300"
                  >
                    <option value="">ΟΛΑ</option>
                    <option value="progress">ΣΕ ΕΞΕΛΙΞΗ</option>
                    <option value="charge">ΠΡΟΣ ΧΡΕΩΣΗ</option>
                    <option value="anamoni">ΣΕ ΑΝΑΜΟΝΗ</option>
                    <option value="none">ΚΕΝΟ</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    value={filters[c.key] || ""}
                    onChange={(e) => setFilter(c.key, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="ΑΝΑΖΗΤΗΣΗ..."
                    data-testid={`table-${variant}-filter-${c.key}`}
                    className={`w-full rounded-md bg-white/95 text-ink text-[11px] font-arial px-1.5 py-1 outline-none border border-white/20 focus:border-amber-300 placeholder:text-ink/30 ${
                      c.align === "right" ? "text-right" : "text-left"
                    }`}
                  />
                )}
              </th>
            ))}
            {actions && (
              <th className="px-1.5 py-1.5 border-t border-white/10">
                {hasAnyFilter && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    data-testid={`table-${variant}-filter-clear`}
                    title="ΚΑΘΑΡΙΣΜΟΣ ΦΙΛΤΡΩΝ"
                    className="w-full rounded-md bg-amber-300 text-ink text-[9px] font-black uppercase tracking-wider px-1 py-1 hover:bg-amber-400 transition-colors"
                  >
                    ΚΑΘΑΡΙΣΜΟΣ
                  </button>
                )}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + (actions ? 1 : 0)}
                className="px-3 py-8 text-center italic text-ink/40"
              >
                ΧΩΡΙΣ ΕΓΓΡΑΦΕΣ
              </td>
            </tr>
          )}
          {sorted.map((n, i) => (
            <tr
              key={n.id}
              data-testid={`table-${variant}-row-${n.id}`}
              className={`${
                i % 2 === 0 ? "bg-white" : "bg-black/[0.018]"
              } hover:bg-amber-50/60 border-t border-black/5`}
            >
              {columns.map((c) => {
                if (c.isStatus) {
                  // Determine dominant background color by priority:
                  // in_progress (red) > needs_payment (yellow) > read/ΑΝΑΜΟΝΗ (orange)
                  let bgClass = "";
                  let textClass = "text-ink/25";
                  if (n.in_progress) {
                    bgClass = "bg-red-600";
                    textClass = "text-white";
                  } else if (n.needs_payment) {
                    bgClass = "bg-yellow-400";
                    textClass = "text-ink";
                  } else if (n.read) {
                    bgClass = "bg-orange-500";
                    textClass = "text-white";
                  }
                  const hasAny = n.in_progress || n.needs_payment || n.read;
                  return (
                    <td
                      key={c.key}
                      style={c.width ? { width: c.width, minWidth: c.width } : undefined}
                      className={`px-2.5 py-1.5 border-r border-black/5 last:border-r-0 ${bgClass} ${textClass}`}
                    >
                      {hasAny ? (
                        <div className="flex flex-col gap-0.5 leading-tight">
                          {n.in_progress && (
                            <span
                              data-testid={`table-badge-progress-${n.id}`}
                              className="text-[10px] font-black uppercase tracking-wider whitespace-nowrap"
                            >
                              ΣΕ ΕΞΕΛΙΞΗ
                              {n.in_progress_by ? ` · ${n.in_progress_by}` : ""}
                            </span>
                          )}
                          {n.needs_payment && (
                            <span
                              data-testid={`table-badge-charge-${n.id}`}
                              className="text-[10px] font-black uppercase tracking-wider whitespace-nowrap"
                            >
                              ΠΡΟΣ ΧΡΕΩΣΗ
                            </span>
                          )}
                          {n.read && (
                            <span
                              data-testid={`table-badge-anamoni-${n.id}`}
                              className="text-[10px] font-black uppercase tracking-wider whitespace-nowrap"
                            >
                              ΣΕ ΑΝΑΜΟΝΗ
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-ink/25 text-xs">—</span>
                      )}
                    </td>
                  );
                }
                const raw = getRowValue(n, c.key);
                const display = c.isDate ? fmtDateTime(raw) : raw;
                return (
                  <td
                    key={c.key}
                    style={c.width ? { width: c.width, minWidth: c.width } : undefined}
                    className={`px-2.5 py-1.5 border-r border-black/5 last:border-r-0 ${
                      c.align === "right" ? "text-right" : "text-left"
                    } ${c.mono ? "tabular-nums font-black text-ink" : ""} ${
                      c.wide ? "max-w-[520px]" : ""
                    }`}
                  >
                    <div
                      className={`truncate ${
                        c.wide ? "whitespace-pre-line break-words line-clamp-2" : ""
                      }`}
                      title={String(display || "")}
                    >
                      {display || (
                        <span className="text-ink/25">—</span>
                      )}
                    </div>
                  </td>
                );
              })}
              {actions && (
                <td className="px-2.5 py-1 text-right">
                  <div className="inline-flex items-center gap-1">
                    {actions(n)}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
